import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { VisibilitySection } from '@provider-directory/hooks/useVisibleSection';
import { ProviderDetailsResource } from '@provider-directory/types/providers.contracts';
import SideMenu from '../../side-menu/side-menu.component';
import { getProviderLinks } from '../links/links.model';

interface ProviderDetailSideMenuProps {
  provider: ProviderDetailsResource;
}

export const ProviderDetailSideMenu: FC<ProviderDetailSideMenuProps> = ({
  provider,
}) => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { featureFlags } = useFeatureFlags();

  const isProviderLinksEnabled = Boolean(featureFlags.isProviderLinksEnabled);

  const menuItems = useMemo<VisibilitySection[]>(() => {
    const sections: VisibilitySection[] = [];

    const { biography, locations, fellowships, residencies, medicalEducation } =
      provider;

    if (biography) {
      sections.push({ id: 'about', title: t('details.about') });
    }

    if (
      [fellowships, residencies, medicalEducation].some(
        (section) => section.length > 0,
      ) ||
      provider.boardCertifications?.length
    ) {
      sections.push({
        id: 'credentials',
        title: t('details.credentials'),
      });
    }

    if (
      provider.clinicalFocus ||
      provider.researchFocus ||
      provider.specialties.length ||
      provider.conditions?.length
    ) {
      sections.push({ id: 'expertise', title: t('details.expertise') });
    }

    sections.push({ id: 'insurance', title: t('details.insurance') });

    if (locations.length > 0) {
      sections.push({ id: 'locations', title: t('details.locations') });
    }

    if (getProviderLinks(provider, isProviderLinksEnabled).length > 0) {
      sections.push({ id: 'links', title: t('details.links') });
    }

    // TODO: Add research section when added to the ProviderDetailsResource API response

    return sections;
  }, [provider, t, isProviderLinksEnabled]);

  return (
    <div
      data-testid={`provider-${provider.npi}-sidemenu`}
      className={
        'sticky top-0 z-[1] min-w-[184px] bg-white pt-6 tablet:pt-10 desktop:relative desktop:mr-4 desktop:pt-0'
      }
    >
      <SideMenu
        menuItems={menuItems}
        className={
          'sticky top-10 overflow-x-auto mobile:flex-row mobile:justify-between desktop:flex-col'
        }
        onItemClick={({ id, title }) => {
          analytics.clickedBioAnchorLinkEvent({
            provider,
            linkContextPage: 'ProviderDetails',
            linkContextComponent: 'SideMenu',
            clickText: title,
            clickURL: `#${id}`,
          });
        }}
      />
    </div>
  );
};
